/**
 * The relay-load charting calendar — a month grid of advertiser contracts for
 * a site's chartable relay loads, rendered on the MUI X Scheduler.
 *
 * Contracts are read-only spans (no drag/resize, no recurrence); the only
 * interaction is a click, intercepted the same way SiteEventCalendar
 * intercepts clicks — a capture-phase listener recovering the contract id
 * from the `odl-cal-evt-<id>` class the scheduler stamps onto each chip.
 *
 * Contains:
 * - ChartingCalendar — the calendar component (default export)
 */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { EventCalendar } from '@mui/x-scheduler/event-calendar';
import { addDays, format, isAfter, isBefore, isEqual, parseISO } from 'date-fns';
import { Map } from 'immutable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import { relayLoadSelector } from '@sophocles/entities/RelayLoad/relayLoadSelectors';
import { editRelayLoadContract } from '@sophocles/features/charting/api';
import {
  eventIdClassName,
  findEventIdFromTarget,
  MONTH_CELL_MIN_HEIGHT,
} from '@sophocles/views/sites/utils/schedulerEventAdapter';

import RelayLoadContractForm from '../forms/RelayLoadContractForm';

const MIN_CALENDAR_HEIGHT = 640;
const ACTIVATION_KEYS = new Set(['Enter', ' ']);
const CONTRACT_STATE_COLOR = { past: 'grey', active: 'green', future: 'blue' };

/** Parse a `YYYY-MM-DD` date-only string as a local date (not UTC). */
function parseDateOnly(dateStr) {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function contractState(startDate, endDate, today) {
  const isActive =
    (isEqual(today, startDate) || isAfter(today, startDate)) && (isEqual(today, endDate) || isBefore(today, endDate));
  if (isActive) {
    return 'active';
  }
  return isBefore(endDate, today) ? 'past' : 'future';
}

function contractTitle(contract, relayLoadName) {
  const base = contract.mapping_name ? `${contract.advertiser} | ${contract.mapping_name}` : contract.advertiser;
  const withLoad = relayLoadName ? `${relayLoadName}: ${base}` : base;
  return contract.override_end_date ? `${withLoad} (override)` : withLoad;
}

function ChartingCalendar({ contracts = [], localTimeZone, onEventClick, visibleLoads = null, onContractsChanged }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useUser();
  const relayLoadsById = useSelector(relayLoadSelector);
  const startOfWeek = user.get('options', Map()).get('start_of_week', 'sunday');
  const weekStartsOn = startOfWeek === 'monday' ? 1 : 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const visibleContracts = useMemo(() => {
    if (visibleLoads == null) {
      return contracts;
    }
    return contracts.filter(contract => visibleLoads.includes(contract.relay_load));
  }, [contracts, visibleLoads]);

  const contractsById = useMemo(
    () => Object.fromEntries(visibleContracts.map(contract => [String(contract.id), contract])),
    [visibleContracts],
  );

  const events = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return visibleContracts.map(contract => {
      const startDate = parseDateOnly(contract.contract_start);
      const endDate = parseDateOnly(contract.contract_end);
      const relayLoadName = relayLoadsById.get(String(contract.relay_load), Map()).get('name', null);
      return {
        id: String(contract.id),
        title: contractTitle(contract, relayLoadName),
        start: contract.contract_start,
        end: format(addDays(parseISO(contract.contract_end), 1), 'yyyy-MM-dd'),
        allDay: true,
        readOnly: true,
        color: CONTRACT_STATE_COLOR[contractState(startDate, endDate, today)],
        className: eventIdClassName(contract.id),
      };
    });
  }, [visibleContracts, relayLoadsById]);

  const handleEventActivate = useCallback(
    contractId => {
      const contract = contractsById[contractId];
      if (!contract) {
        return;
      }
      if (onEventClick) {
        onEventClick(contract);
      } else {
        setSelectedContract(contract);
        setDialogOpen(true);
      }
    },
    [contractsById, onEventClick],
  );

  useEffect(() => {
    const intercept = domEvent => {
      const id = findEventIdFromTarget(domEvent.target);
      if (!id) {
        return;
      }
      domEvent.stopImmediatePropagation();
      domEvent.preventDefault();
      handleEventActivate(id);
    };
    const interceptKey = domEvent => {
      if (ACTIVATION_KEYS.has(domEvent.key)) {
        intercept(domEvent);
      }
    };
    document.addEventListener('click', intercept, true);
    document.addEventListener('keydown', interceptKey, true);
    return () => {
      document.removeEventListener('click', intercept, true);
      document.removeEventListener('keydown', interceptKey, true);
    };
  }, [handleEventActivate]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        {t('charting.chartingCalendar')}
      </Typography>
      <CalendarLegend />
      <Box
        sx={{
          minHeight: MIN_CALENDAR_HEIGHT,
          '& .MuiEventCalendar-monthViewBody': { gridAutoRows: `minmax(${MONTH_CELL_MIN_HEIGHT}px, 1fr)` },
        }}
      >
        <EventCalendar
          events={events}
          views={['month']}
          defaultView="month"
          defaultPreferences={{ weekStartsOn }}
          eventCreation={false}
        />
      </Box>
      {selectedContract && (
        <RelayLoadContractForm
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedContract(null);
          }}
          onSubmit={async values => {
            const response = await dispatch(editRelayLoadContract({ id: selectedContract.id, ...values })).unwrap();
            setDialogOpen(false);
            onContractsChanged?.();
            return response;
          }}
          contract={selectedContract}
          loadId={selectedContract.relay_load}
          localTimeZone={localTimeZone}
          existingContracts={contracts.filter(
            c => c.relay_load == selectedContract.relay_load && c.id != selectedContract.id,
          )}
          editMode={true}
        />
      )}
    </Box>
  );
}

function CalendarLegend() {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
      <LegendItem color="grey.300" label={t('charting.legend.pastContract')} />
      <LegendItem color="success.light" label={t('charting.legend.activeContract')} />
      <LegendItem color="primary.light" label={t('charting.legend.futureContract')} />
    </Box>
  );
}

function LegendItem({ color, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: 'var(--radius-md)' }} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}

export default ChartingCalendar;
